import { memo, useEffect, type RefObject } from 'react';
import { useInView } from 'react-intersection-observer';

interface ScrollClassTriggerProps {
  /**
   * Reference to the element that will receive the class changes
   */
  targetRef: RefObject<HTMLElement>;
  /**
   * Class name to apply when the trigger div is in view
   */
  inViewClassName?: string;
  /**
   * Class name to apply when the trigger div is out of view
   */
  outOfViewClassName?: string;
}

/**
 * ScrollClassTrigger
 *
 * A component that observes a trigger div's visibility in the viewport
 * and applies CSS classes to a target element based on that visibility.
 *
 * This is useful for adding/removing shadows or other visual effects
 * when scrolling past certain points in the page.
 */
export const ScrollClassTrigger = memo<ScrollClassTriggerProps>(
  ({ targetRef, inViewClassName, outOfViewClassName }) => {
    const { inView, ref } = useInView({
      triggerOnce: false,
    });

    useEffect(() => {
      if (!targetRef.current) return;

      if (inView) {
        if (outOfViewClassName) {
          targetRef.current.classList.remove(outOfViewClassName);
        }
        if (inViewClassName) {
          targetRef.current.classList.add(inViewClassName);
        }
      } else {
        if (inViewClassName) {
          targetRef.current.classList.remove(inViewClassName);
        }
        if (outOfViewClassName) {
          targetRef.current.classList.add(outOfViewClassName);
        }
      }
    }, [inView, targetRef, inViewClassName, outOfViewClassName]);

    return <div data-observer-intercept ref={ref} />;
  }
);

ScrollClassTrigger.displayName = 'ScrollClassTrigger';
